import { defaultLocale } from 'constants/locale';
import { EventIds } from 'constants/eventIds';
import { useEffect } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { useAuthentication } from 'contexts/AuthenticationProvider/AuthenticationProvider';
import { log } from 'utils';
import { i18n } from 'utils/setupTranslations';
import { getIsSupportedLocale } from 'helpers/locale/getIsSupportedLocale';
import { getIsUnsupportedLocale } from 'helpers/locale/getIsUnsupportedLocale';
import { navigateIfNeeded } from './helpers/navigateIfNeeded';

export const useRemoveLocaleFromUrl = () => {
    const match = useMatch('/:locale/*');
    const pathname = match?.pathname;
    const urlLocale = match?.params.locale;
    const navigate = useNavigate();
    const { isLoading, isAuthenticated } = useAuthentication();

    const { locale } = i18n;

    useEffect(() => {
        if (isLoading || !locale) {
            return;
        }

        const supportedUrlLanguage = getIsSupportedLocale(urlLocale)
            ? urlLocale
            : undefined;
        const unsupportedUrlLanguage = getIsUnsupportedLocale(urlLocale)
            ? urlLocale
            : undefined;
        const urlLanguage = supportedUrlLanguage || unsupportedUrlLanguage;

        // user preferred language has highest priority.
        // We remove the locale from the path if present.
        if (isAuthenticated) {
            navigateIfNeeded({
                remove: urlLanguage,
                navigate,
                pathname: pathname,
            });
        }
        // we fallback to locale in the url if present.
        else if (supportedUrlLanguage) {
            navigateIfNeeded({
                remove:
                    supportedUrlLanguage === defaultLocale
                        ? supportedUrlLanguage
                        : undefined,
                navigate,
                pathname: pathname,
            });
        }
        // last fallback is to use default locale.
        else {
            navigateIfNeeded({
                remove: unsupportedUrlLanguage,
                navigate,
                pathname: pathname,
            });

            log.warn(
                'Unsupported locale activation requested, fallback to default locale',
                EventIds.UnsupportedLocale,
                undefined,
                { unsupportedUrlLanguage },
            );
        }
    }, [urlLocale, pathname, isAuthenticated, navigate, locale, isLoading]);
};
